import { useMemo } from 'react';

import { type UniversalCreateMenuSection } from '@atlassian/universal-create/types';
import { useIntl } from 'react-intl';

import { useUserHasCapabilities } from '@townsquare/capability-provider/useUserHasCapabilities';
import { filterUndefined } from '@townsquare/filter-type';
import { useGoalCapabilities } from '@townsquare/goal-capabilities';
import { useUniversalCreateItems as useBaseUniversalCreateItems } from '@townsquare/navigation/universal-create/items';
import { type UseUniversalCreateItemsProps } from '@townsquare/navigation/universal-create/types';
import { useProjectCapabilities } from '@townsquare/project-capabilities';
import { fg } from '@townsquare/stat-sig/gate';

import { useGoalUniversalCreateItems } from './use-goal-universal-create-items';

export const useUniversalCreateItems = (): UseUniversalCreateItemsProps => {
	const intl = useIntl();
	const { teamCreate, goalCreate, projectCreate } = useBaseUniversalCreateItems();
	const userHasGoalsAccess = useUserHasCapabilities(['goals_app:access'], true);
	const userHasProjectsAccess = useUserHasCapabilities(['projects_app:access'], true);
	const projectCapabilities = useProjectCapabilities();
	const canCreateProject = fg('townsquare_m3_group_permissions') ? projectCapabilities.canCreateProject : true;
	const { canCreateGoal } = useGoalCapabilities();
	const canCreateGoalItem = fg('townsquare_m3_group_permissions') ? canCreateGoal : true;
	const goalUniversalCreateItems = useGoalUniversalCreateItems();
	const isSuccessMeasuresEnabled = fg('townsquare_goals_success_measures_prod');

	const suggestionItems = useMemo(
		() => [teamCreate, userHasProjectsAccess && canCreateProject ? projectCreate : undefined],
		[teamCreate, projectCreate, userHasProjectsAccess, canCreateProject],
	);

	// The new success measures universal create experience in the goals app shows create options for each goal type
	const goalItems = useMemo(() => {
		if (!canCreateGoalItem) {
			return [];
		}
		return isSuccessMeasuresEnabled ? goalUniversalCreateItems : [goalCreate];
	}, [goalCreate, isSuccessMeasuresEnabled, goalUniversalCreateItems, canCreateGoalItem]);

	const filteredSuggestionItems = suggestionItems.filter(filterUndefined);

	const items = useMemo<UniversalCreateMenuSection[]>(() => {
		const sections: UniversalCreateMenuSection[] = [];

		if (userHasGoalsAccess && goalItems.length > 0) {
			sections.push({
				type: 'appDefinedSection' as const,
				items: goalItems.filter(filterUndefined),
				foldable: isSuccessMeasuresEnabled,
			});
		}

		if (filteredSuggestionItems.length > 0) {
			sections.push({
				type: 'appDefinedSection' as const,
				heading: intl.formatMessage({
					id: 'townsquare.navigation.universal-create-button.section-title',
					description: 'Displays the other create options',
					defaultMessage: 'Suggestions',
				}),
				items: filteredSuggestionItems,
			});
		}

		return sections;
	}, [intl, userHasGoalsAccess, filteredSuggestionItems, goalItems, isSuccessMeasuresEnabled]);

	return { items, compact: !!suggestionItems.filter(filterUndefined).length };
};
